import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { Link } from 'react-router';
import { RoadtripNationVideoCard, type JWPlayerInstance } from '@/components/Video/RoadtripNationVideoCard';
import { convertToEmbedUrl, getIsYouTubeUrl } from '@/components/Video/VideoUtils';
import type { UnifiedVideoItem } from '@/components/Video/unifiedVideoTypes';
import { VideoPlayIcon } from '@/components/Video/VideoPlayIcon';
import { HUB_DARK_TEXT_CLASS } from '@/components/Hub/hubTheme';
import { storyDetailPath } from '@/pages/Explore/Stories/storyRoutes';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { cn } from '@/lib/utils';

/** Delay before an on-screen card starts its silent preview, and how long that preview runs. */
const PREVIEW_START_DELAY_MS = 3000;
const PREVIEW_MAX_MS = 20000;

/** Below this the card counts as mostly outside the viewport and stops playing. */
const ON_SCREEN_RATIO = 0.5;

// Not the same question as which slide the carousel selected. The Hub is a tall scrolling sheet,
// so a selected slide can be scrolled out of sight and carry on playing audio at someone.
const useIsMostlyOnScreen = (ref: RefObject<Element | null>): boolean => {
    const [isOnScreen, setIsOnScreen] = useState(false);

    useEffect(() => {
        const element = ref.current;
        // jsdom has no IntersectionObserver. Treat "cannot tell" as visible so playback is never
        // dead in an environment that simply has no answer.
        if (!element || typeof IntersectionObserver === 'undefined') {
            setIsOnScreen(true);
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => setIsOnScreen(entry.intersectionRatio >= ON_SCREEN_RATIO),
            { threshold: [0, ON_SCREEN_RATIO, 1] },
        );
        observer.observe(element);
        return () => observer.disconnect();
    }, [ref]);

    return isOnScreen;
};

export interface HubStoryCardProps {
    item: UnifiedVideoItem;
    /** True while this card is the selected carousel slide. */
    isActive?: boolean;
}

/**
 * Previews muted, since browsers block anything else, and plays properly when the user asks.
 * Every control comes from the player's own bar, which is where the keyboard shortcuts already
 * live. A second set laid over the top would fight all three players.
 */
export const HubStoryCard = ({ item, isActive = false }: HubStoryCardProps) => {
    const prefersReducedMotion = usePrefersReducedMotion();
    const frameRef = useRef<HTMLDivElement>(null);
    const nativeVideoRef = useRef<HTMLVideoElement>(null);
    const isOnScreen = useIsMostlyOnScreen(frameRef);

    const [isPreviewing, setIsPreviewing] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);

    const mayPlay = isActive && isOnScreen;

    useEffect(() => {
        if (!mayPlay || isPlaying || prefersReducedMotion) {
            setIsPreviewing(false);
            return;
        }
        const startTimer = setTimeout(() => setIsPreviewing(true), PREVIEW_START_DELAY_MS);
        return () => clearTimeout(startTimer);
    }, [mayPlay, isPlaying, prefersReducedMotion]);

    useEffect(() => {
        if (!isPreviewing) {
            return;
        }
        const stopTimer = setTimeout(() => setIsPreviewing(false), PREVIEW_MAX_MS);
        return () => clearTimeout(stopTimer);
    }, [isPreviewing]);

    // Toggles rather than plays: a second click stops it rather than expanding it.
    const togglePlay = useCallback(() => {
        setIsPreviewing(false);
        setIsPlaying(current => !current);
    }, []);

    const isLive = isPlaying || isPreviewing;

    // A real play owns the frame from then on so pausing shows the paused player rather than dropping the card back to its thumbnail.
    const [hasStarted, setHasStarted] = useState(false);
    useEffect(() => {
        if (isPlaying) {
            setHasStarted(true);
        }
    }, [isPlaying]);

    const isYouTube = item.sourceType === 'other' && getIsYouTubeUrl(item.data.videoUrl);
    /** A plain media file. CareerOneStop serves .mp4 so this is the only way it plays at all. */
    const isNativeFile = item.sourceType === 'other' && !isYouTube;

    /**
     * What this component last asked the element to do. The element reports our own preview and a
     * real press identically, and reading the first as intent would unmute a video nobody asked to
     * hear. Cleared by the next event so a no-op call cannot swallow a later real one.
     */
    const selfDrivenRef = useRef<'playing' | 'paused' | null>(null);

    useEffect(() => {
        const element = nativeVideoRef.current;
        if (!element) {
            return;
        }
        if (isLive && mayPlay) {
            // A preview has nobody behind it so it is always silent. A real play keeps whatever
            // the user last chose on the control bar.
            if (!isPlaying) {
                element.muted = true;
            }
            selfDrivenRef.current = 'playing';
            element.play().catch(() => {
                // Autoplay refused (unmuted preview, data-saver). Nothing will arrive to clear the flag, and the user's own next press would read as this call.
                // Only this call's own mark is cleared since the catch lands a microtask after anything set since.
                if (selfDrivenRef.current === 'playing') {
                    selfDrivenRef.current = null;
                }
            });
        }
        else {
            selfDrivenRef.current = 'paused';
            element.pause();
        }
    }, [isLive, mayPlay, isPlaying]);

    const handleNativePlay = useCallback(() => {
        const wasSelfDriven = selfDrivenRef.current === 'playing';
        selfDrivenRef.current = null;
        if (wasSelfDriven) {
            return;
        }
        // Someone pressed play so this is no longer a silent preview.
        if (nativeVideoRef.current) {
            nativeVideoRef.current.muted = false;
        }
        setIsPreviewing(false);
        setIsPlaying(true);
    }, []);

    const handleNativePause = useCallback(() => {
        const wasSelfDriven = selfDrivenRef.current === 'paused';
        selfDrivenRef.current = null;
        if (wasSelfDriven) {
            return;
        }
        setIsPreviewing(false);
        setIsPlaying(false);
    }, []);

    // Reads the current value inside the updater so these handlers stay stable across renders,
    // which the JW subscriptions below need.
    const promoteToRealPlay = useCallback(() => {
        setIsPreviewing((wasPreviewing) => {
            if (wasPreviewing) {
                setIsPlaying(true);
            }
            return false;
        });
    }, []);

    // The only mute this component writes during a preview is the silencing one so an element that
    // comes back unmuted was unmuted by the user.
    const handleNativeVolumeChange = useCallback(() => {
        if (nativeVideoRef.current?.muted === false) {
            promoteToRealPlay();
        }
    }, [promoteToRealPlay]);

    // JW owns its control bar so these are the only sign the user engaged. `mute` carries which
    // way it went, since promoting unmutes and doing that to someone who just muted is its own bug.
    const handleJwPlayerReady = useCallback((player: JWPlayerInstance) => {
        player.on('mute', (event) => {
            if (event?.mute === false) {
                promoteToRealPlay();
            }
        });
        player.on('seek', promoteToRealPlay);
        player.on('fullscreen', promoteToRealPlay);
    }, [promoteToRealPlay]);

    return (
        <figure
            className={cn(
                'group flex h-full flex-col gap-2 rounded-xl p-1 transition-colors',
                'light:hover:bg-gz-sage-100 dark:hover:bg-gz-blue-700',
                // focus-within, not focus-visible. The focusable thing is the player or the play
                // button inside, and the ring goes around the whole card.
                'focus-within:ring-2 focus-within:ring-gz-bluebell-500',
            )}
        >
            <div
                ref={frameRef}
                className="relative aspect-video w-full overflow-hidden rounded-xl bg-muted"
            >
                {item.sourceType === 'roadtripNation' && isLive
                    ? (
                            <RoadtripNationVideoCard
                                jwAssetId={item.data.jwAssetId}
                                videoUrl={item.data.mp4Url}
                                thumbUrl={item.thumbUrl}
                                captionsUrl={item.data.captionsUrl}
                                // Both halves of the same signal. The player's `paused` only ever pauses so a story
                                // scrolled back into view is restarted by `autostart` rising and a literal there never rises.
                                autostart={mayPlay}
                                muted={!isPlaying}
                                paused={!mayPlay}
                                onPlayerReady={handleJwPlayerReady}
                                // `sm:p-0` as well as `p-0`. tailwind-merge drops only the
                                // unprefixed half of the Card's padding, letterboxing the player.
                                className="size-full border-0 bg-transparent p-0 sm:p-0"
                            />
                        )
                    : isYouTube && isLive
                        ? (
                                // The preview cap is unconditional here. Clicks inside a
                                // cross-origin iframe never reach us, so engagement is invisible.
                                <iframe
                                    className="size-full"
                                    src={convertToEmbedUrl(item.data.videoUrl, { autoplay: true, captions: true })}
                                    title={item.title}
                                    referrerPolicy="strict-origin-when-cross-origin"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            )
                        : isNativeFile && (isLive || hasStarted)
                            ? (
                                    // No `onClick`. The control bar sits in the element's shadow tree and retargets its
                                    // clicks here, so a handler would fire for mute, fullscreen and scrubbing too.
                                    <video
                                        ref={nativeVideoRef}
                                        key={item.data.videoUrl}
                                        src={item.data.videoUrl}
                                        controls
                                        playsInline
                                        preload="metadata"
                                        muted={!isPlaying}
                                        onPlay={handleNativePlay}
                                        onPause={handleNativePause}
                                        onVolumeChange={handleNativeVolumeChange}
                                        onSeeking={promoteToRealPlay}
                                        aria-label={item.title}
                                        className="size-full object-cover"
                                    />
                                )
                            : (
                                    <button
                                        type="button"
                                        onClick={togglePlay}
                                        aria-label={`Play ${item.title}`}
                                        className={cn(
                                            'size-full',
                                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gz-bluebell-500',
                                        )}
                                        data-track-click="Hub Story Played"
                                        data-track-location="hub-panel"
                                        data-track-button-name="hub-story"
                                    >
                                        {item.thumbUrl
                                            ? (
                                                    <img
                                                        src={item.thumbUrl}
                                                        alt=""
                                                        className="size-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                                                    />
                                                )
                                            : isNativeFile
                                                ? (
                                                        // No `controls`, so this draws no play button of its own behind ours.
                                                        <video
                                                            src={item.data.videoUrl}
                                                            preload="metadata"
                                                            muted
                                                            playsInline
                                                            className="size-full object-cover"
                                                        />
                                                    )
                                                : null}
                                        <VideoPlayIcon className="pointer-events-none absolute inset-0 m-auto size-fit !text-[6.25rem]" />
                                    </button>
                                )}
            </div>
            <figcaption className={cn('flex flex-col gap-0.5', HUB_DARK_TEXT_CLASS)}>
                {/* Two targets, as on the browse grid: the title opens the story, the video plays. */}
                {item.storyId
                    ? (
                            <Link
                                to={storyDetailPath(item.storyId)}
                                title={item.title}
                                className={cn(
                                    'type-body-small line-clamp-1 font-medium hover:underline',
                                    'rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gz-bluebell-500',
                                )}
                                data-track-click="Story Opened"
                                data-track-location="hub-panel"
                                data-track-button-name="hub-story-title"
                            >
                                {item.title}
                            </Link>
                        )
                    : (
                            <span className="type-body-small line-clamp-1 font-medium" title={item.title}>
                                {item.title}
                            </span>
                        )}
                <span className={cn('type-body-small text-muted-foreground', HUB_DARK_TEXT_CLASS)}>
                    {item.creator}
                </span>
            </figcaption>
        </figure>
    );
};
